/**
 * ON-DEVICE VIEWPORT PROBE — staging-only diagnostic overlay.
 *
 * WHY IT EXISTS: three headless sweeps in a row reported "board does not resize" while two real
 * phones plainly showed it resizing. Every one of those sweeps had to *assume* what the Telegram
 * WebView reports for the viewport, and the assumption (that Telegram's stable height ignores the
 * keyboard) is exactly the thing under test. A desktop browser cannot answer it: there is no real
 * soft keyboard, no Telegram bridge and no native WebView resize. So stop reconstructing and read
 * the numbers off the device — the same lesson as the `POST /client-error` beacon.
 *
 * It captures TWO labelled columns:
 *   REST — refreshed continuously while NO text field is focused, then FROZEN the moment one is,
 *          so it holds the "as if no keyboard" truth to compare against;
 *   NOW  — live.
 * A row is flagged « ! » when NOW differs from REST. One screenshot with the keyboard up therefore
 * shows exactly which of the platform's height signals moved and which held.
 *
 * GATING: OPT-IN ONLY — `?vpdebug=1`. It can never appear on prod (info.limoozin.info /
 * game.limoozin.info / top. / app.binggames.top).
 *
 * ⚠️ IT USED TO MOUNT ITSELF ON EVERY `staging.*` LOAD, and it covers more than half a phone
 * screen — which made staging unusable for looking at anything else (reported 2026-08-06). A
 * diagnostic that hides the thing being diagnosed is worse than no diagnostic. The panel is
 * unchanged and one query param away; it simply no longer shows up uninvited.
 */
import { useEffect, useRef, useState } from "react";
import { viewport } from "@telegram-apps/sdk";
import { resolveLaunchContext } from "../telegram/launch";
import { reflowBumpCount } from "../telegram/viewport";

export function viewportProbeEnabled(_hostname: string, search: string): boolean {
  return new URLSearchParams(search).get("vpdebug") === "1";
}

/** One reading of every height signal that could plausibly move when the keyboard opens. */
type Sample = {
  /** SDK `viewport.stableHeight()` — the value `syncKeyboardInset` treats as keyboard-independent. */
  tgStable: string;
  /** SDK `viewport.height()` — the live one. */
  tgLive: string;
  /** What `viewport.bindCssVars()` actually published on <html>. */
  cssStableVar: string;
  vvHeight: string;
  vvOffsetTop: string;
  docClientH: string;
  innerH: string;
  /** Resolved via probe elements — what the CSS variables really compute to right now. */
  appH: string;
  gameH: string;
  /** The live game table's own height, when a table is on screen. */
  tableH: string;
  /** The board itself (منچ / chess) — the thing the player actually watches. */
  boardH: string;
  /** WHERE the board is. A jolt with no size change is a POSITION change, and the rest/now columns
   *  measured only SIZE — so it was structurally invisible here. */
  boardY: string;
  /** Any document/ancestor scrolling. `scrollIntoView` on a focused composer can scroll the root
   *  even with `overflow:hidden`, and iOS reports its keyboard shift via visualViewport.offsetTop. */
  scrollTop: string;
  /** The chat sheet: `min(64vh, 480px)` + 32px chrome. Its height MEASURES the layout viewport. */
  sheet: string;
  /**
   * THE CHAIN. The board's SIZE and the table's HEIGHT are provably frozen, yet BOARD y moved 10px
   * on an iPhone — so the displacement is produced by something BETWEEN them. These are the heights
   * of the table root's own children, and then of the board column's children: whichever number
   * differs rest-vs-typing IS the culprit, localised to one node without another round of guessing.
   */
  kids: string;
  colKids: string;
  /** The SHELL. MUST move with the keyboard: it is what lifts the nav + chat composer. */
  htmlH: string;
  /** The inline pin the board freeze holds on <html>; "(none)" at rest. */
  pin: string;
  kbdAttr: string;
  safeTop: string;
  focused: string;
};

const ROWS: Array<[keyof Sample, string]> = [
  ["tgStable", "sdk.stableHeight"],
  ["tgLive", "sdk.height"],
  ["cssStableVar", "--tg-stable var"],
  ["vvHeight", "visualViewport.h"],
  ["vvOffsetTop", "visualViewport.top"],
  ["docClientH", "docEl.clientH"],
  ["innerH", "innerHeight"],
  ["appH", "--app-h"],
  ["gameH", "--game-h"],
  ["tableH", "table height"],
  ["boardH", "BOARD size"],
  ["boardY", "BOARD y ⟵new"],
  ["scrollTop", "scroll doc/body/win"],
  ["sheet", "chat sheet h@y"],
  ["kids", "table kids h ⟵new"],
  ["colKids", "board col kids ⟵new"],
  ["htmlH", "html height"],
  ["pin", "freeze pin"],
  ["kbdAttr", "data-keyboard"],
  ["safeTop", "safe top"],
  ["focused", "focused el"],
];

function num(read: () => number): string {
  try {
    const v = read();
    return Number.isFinite(v) ? String(Math.round(v)) : "?";
  } catch {
    return "ERR"; // the signal threw — which is itself the answer (unmounted / unavailable)
  }
}

function read(probes: { app: HTMLElement | null; game: HTMLElement | null }): Sample {
  const docEl = document.documentElement;
  const vv = window.visualViewport;
  // The live table root: React writes `height: var(--game-h)` into its inline style attribute.
  //
  // ⚠️ SCOPED TO #root DELIBERATELY. Unscoped, this ALSO matches <html> the moment
  // `startBoardHeightFreeze()` pins `--game-h` there — and <html> is `height: var(--app-h)`, so the
  // row silently switched from reporting the TABLE to reporting the SHELL exactly when the keyboard
  // opened, which read as "the table still shrinks" when it did not. Keep the #root scope.
  const table = document.querySelector<HTMLElement>('#root [style*="--game-h"]');
  const active = document.activeElement;
  return {
    tgStable: num(() => viewport.stableHeight()),
    tgLive: num(() => viewport.height()),
    cssStableVar: docEl.style.getPropertyValue("--tg-viewport-stable-height").trim() || "(unset)",
    vvHeight: vv ? String(Math.round(vv.height)) : "none",
    vvOffsetTop: vv ? String(Math.round(vv.offsetTop)) : "none",
    docClientH: String(docEl.clientHeight),
    innerH: String(Math.round(window.innerHeight)),
    appH: probes.app ? String(Math.round(probes.app.getBoundingClientRect().height)) : "?",
    gameH: probes.game ? String(Math.round(probes.game.getBoundingClientRect().height)) : "?",
    tableH: table ? String(Math.round(table.getBoundingClientRect().height)) : "(no table)",
    boardH: (() => {
      const b = document.querySelector('[data-testid="mensch-board"], [aria-label="chess board"]');
      return b ? String(Math.round(b.getBoundingClientRect().height)) : "-";
    })(),
    boardY: (() => {
      const b = document.querySelector('[data-testid="mensch-board"], [aria-label="chess board"]');
      return b ? String(Math.round(b.getBoundingClientRect().y)) : "-";
    })(),
    scrollTop: `${Math.round(docEl.scrollTop)}/${Math.round(document.body.scrollTop)}/${Math.round(window.scrollY)}`,
    sheet: (() => {
      const panel = document.querySelector('[role="dialog"] > div');
      if (!panel) return "(closed)";
      const r = panel.getBoundingClientRect();
      return `${Math.round(r.height)}@y${Math.round(r.y)}`;
    })(),
    kids: table
      ? [...table.children].map((c) => Math.round(c.getBoundingClientRect().height)).join("·")
      : "-",
    colKids: (() => {
      const b = document.querySelector('[data-testid="mensch-board"], [aria-label="chess board"]');
      const col = b?.closest("div")?.parentElement;
      return col
        ? [...col.children].map((c) => Math.round(c.getBoundingClientRect().height)).join("·")
        : "-";
    })(),
    htmlH: String(Math.round(docEl.getBoundingClientRect().height)),
    pin: docEl.style.getPropertyValue("--game-h") || "(none)",
    kbdAttr: docEl.dataset["keyboard"] ?? "(unset)",
    safeTop: getComputedStyle(docEl).getPropertyValue("--tg-safe-top").trim() || "0px",
    focused: active && active !== document.body ? active.tagName.toLowerCase() : "-",
  };
}

/** True while a text field owns focus — on a phone that IS the keyboard, whatever the heights say. */
function isTyping(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || (el as HTMLElement).isContentEditable === true;
}

/**
 * The CLOSE window. The rest/now pair only ever described the keyboard being OPEN — the rest column
 * freezes at focus and never re-samples — so a board that misbehaves as the keyboard goes AWAY was
 * invisible to this probe. And the symptom is transient by description ("it comes back"), so a
 * third snapshot would not be enough either: the whole window is sampled, at frame rate, and the
 * EXTREMES are reported.
 */
const CLOSE_WINDOW_MS = 2500;
/**
 * A frame where the element was NOT FOUND is counted, never folded into the band.
 *
 * It used to be recorded as -1, and since the band keeps a MINIMUM, one absent frame made the whole
 * line read `-1..932` forever — indistinguishable from a real collapse, and unreadable either way.
 * "Absent for a frame" and "moved" are different diagnoses (an unmounted subtree vs. a layout
 * change), so they get different counters.
 */
type Band = { min: number; max: number };
const band = (b: Band | null, v: number): Band =>
  b === null ? { min: v, max: v } : { min: Math.min(b.min, v), max: Math.max(b.max, v) };
const showBand = (b: Band | null): string =>
  b === null ? "-" : b.min === b.max ? String(b.min) : `${b.min}..${b.max}`;

export function ViewportProbe(): React.ReactNode {
  const [open, setOpen] = useState(true);
  const [now, setNow] = useState<Sample | null>(null);
  const [rest, setRest] = useState<Sample | null>(null);
  const [close, setClose] = useState<{
    table: Band | null;
    board: Band | null;
    y: Band | null;
    renders: number;
    missing: number;
  } | null>(null);
  const probes = useRef<{ app: HTMLElement | null; game: HTMLElement | null }>({
    app: null,
    game: null,
  });

  useEffect(() => {
    // Two off-screen probes styled exactly like the real consumers, so the panel reports what the
    // CSS variables RESOLVE to (getComputedStyle on a custom property returns the literal `min(…)`).
    const mk = (v: string): HTMLElement => {
      const d = document.createElement("div");
      d.style.cssText = `position:fixed;left:-9999px;top:0;width:1px;pointer-events:none;height:var(${v})`;
      document.body.appendChild(d);
      return d;
    };
    probes.current = { app: mk("--app-h"), game: mk("--game-h") };

    let raf = 0;
    let wasTyping = false;
    let closeUntil = 0;
    let bumpsAtClose = 0;
    const tick = (): void => {
      const s = read(probes.current);
      setNow(s);
      const typing = isTyping();
      // REST is the "no keyboard" reference: keep refreshing it until a field takes focus, then
      // freeze — so the comparison survives however the platform reports the keyboard.
      if (!typing && Date.now() >= closeUntil) setRest(s);

      if (wasTyping && !typing) {
        // Focus just ended = the dismissal begins. Record the whole transition.
        closeUntil = Date.now() + CLOSE_WINDOW_MS;
        bumpsAtClose = reflowBumpCount();
        setClose({ table: null, board: null, y: null, renders: 0, missing: 0 });
      }
      wasTyping = typing;

      const inWindow = Date.now() < closeUntil;
      if (inWindow) {
        const el = document.querySelector('#root [style*="--game-h"]');
        const b = document.querySelector(
          '[data-testid="mensch-board"], [aria-label="chess board"]',
        );
        const th = el ? Math.round(el.getBoundingClientRect().height) : -1;
        const bh = b ? Math.round(b.getBoundingClientRect().height) : -1;
        const by = b ? Math.round(b.getBoundingClientRect().y) : -1;
        const found = el !== null && b !== null;
        setClose((c) => ({
          table: found ? band(c?.table ?? null, th) : (c?.table ?? null),
          board: found ? band(c?.board ?? null, bh) : (c?.board ?? null),
          y: found ? band(c?.y ?? null, by) : (c?.y ?? null),
          renders: reflowBumpCount() - bumpsAtClose,
          missing: (c?.missing ?? 0) + (found ? 0 : 1),
        }));
      }
      // Frame rate through the close (a transient lasts a few frames), lazy otherwise.
      raf = inWindow
        ? (requestAnimationFrame(tick) as unknown as number)
        : window.setTimeout(() => requestAnimationFrame(tick), 250);
    };
    tick();
    return () => {
      window.clearTimeout(raf);
      probes.current.app?.remove();
      probes.current.game?.remove();
    };
  }, []);

  if (!now) return null;
  const platform = resolveLaunchContext().platform ?? "?";
  if (!open)
    return (
      <button
        onClick={() => setOpen(true)}
        style={{ ...CHIP, top: 4, insetInlineStart: 4 }}
        aria-label="show viewport probe"
      >
        VP
      </button>
    );

  return (
    <div dir="ltr" style={PANEL}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
        <b>
          VIEWPORT PROBE · {platform}
          {isTyping() ? " · TYPING" : ""}
        </b>
        <button onClick={() => setOpen(false)} style={CHIP} aria-label="hide viewport probe">
          ×
        </button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "auto auto auto auto", columnGap: 6 }}>
        <span style={HEAD}>signal</span>
        <span style={HEAD}>rest</span>
        <span style={HEAD}>now</span>
        <span style={HEAD} />
        {ROWS.map(([k, label]) => {
          const a = rest ? rest[k] : "-";
          const b = now[k];
          const moved = rest !== null && a !== b;
          return <Row key={k} label={label} a={a} b={b} moved={moved} />;
        })}
      </div>
      <div style={{ marginTop: 3, borderTop: "1px solid #34473d", paddingTop: 2 }}>
        <b>CLOSE transition</b> (sampled every frame for {CLOSE_WINDOW_MS / 1000}s after focus ends)
        <div>
          table {showBand(close?.table ?? null)} · BOARD {showBand(close?.board ?? null)} · board y{" "}
          {showBand(close?.y ?? null)} · root re-renders {close?.renders ?? "-"} · missing frames{" "}
          {close?.missing ?? "-"}
        </div>
      </div>
    </div>
  );
}

function Row({
  label,
  a,
  b,
  moved,
}: {
  label: string;
  a: string;
  b: string;
  moved: boolean;
}): React.ReactNode {
  const c = moved ? "#ff8a80" : "#cfe9d8";
  return (
    <>
      <span style={{ color: "#9bb3a6" }}>{label}</span>
      <span style={{ color: c, textAlign: "right" }}>{a}</span>
      <span style={{ color: c, textAlign: "right" }}>{b}</span>
      <span style={{ color: "#ff8a80" }}>{moved ? "!" : ""}</span>
    </>
  );
}

const PANEL: React.CSSProperties = {
  position: "fixed",
  top: 0,
  insetInlineStart: 0,
  zIndex: 2147483647,
  background: "rgba(0,0,0,.86)",
  color: "#cfe9d8",
  font: "10px/1.35 ui-monospace,Menlo,Consolas,monospace",
  padding: "4px 6px",
  borderBottomRightRadius: 8,
  maxWidth: "78vw",
  pointerEvents: "auto",
};

const CHIP: React.CSSProperties = {
  appearance: "none",
  border: "1px solid #4a6357",
  background: "rgba(0,0,0,.86)",
  color: "#cfe9d8",
  font: "10px ui-monospace,monospace",
  borderRadius: 6,
  padding: "1px 6px",
  cursor: "pointer",
  position: "relative",
  zIndex: 2147483647,
};

const HEAD: React.CSSProperties = { color: "#7f9a8b", borderBottom: "1px solid #34473d" };
